import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import JobCard from "@/components/JobCard";
import JobDetailsModal from "@/components/JobDetailsModal";
import AddJobModal from "@/components/AddJobModal";
import PaywallModal from "@/components/PaywallModal";
import { Button } from "@/components/ui/button";
import { Plus, Briefcase, Building2, Loader2 } from "lucide-react";

const COLUMNS = [
  { id: "SAVED", title: "Saved", color: "#94A3B8" },
  { id: "APPLIED", title: "Applied", color: "#A3C3D8" },
  { id: "INTERVIEWING", title: "Interviewing", color: "#F6AD55" },
  { id: "OFFER", title: "Offer", color: "#68D391" },
  { id: "REJECTED", title: "Rejected", color: "#FC8181" }
];

export default function Dashboard() {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [quota, setQuota] = useState(null);

  const fetchApplications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, [token]);

  const fetchQuota = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payments/quota`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuota(response.data);
    } catch (error) {
      console.error("Failed to fetch quota:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchApplications();
    fetchStats();
    fetchQuota();
  }, [fetchApplications, fetchStats, fetchQuota]);

  const getColumnApplications = (columnId) => {
    return applications.filter(app => app.status === columnId);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { draggableId, destination } = result;
    const newStatus = destination.droppableId;

    // Find the application
    const app = applications.find(a => a.id === draggableId);
    if (!app || app.status === newStatus) return;

    // Optimistic update
    setApplications(prev => 
      prev.map(a => a.id === draggableId ? { ...a, status: newStatus } : a)
    );

    try {
      await axios.patch(
        `${API}/applications/${draggableId}/status`,
        { status: newStatus, trigger_type: "drag_drop" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Moved to ${newStatus.toLowerCase()}`);
      fetchStats();
    } catch (error) {
      // Revert on error
      setApplications(prev => 
        prev.map(a => a.id === draggableId ? { ...a, status: app.status } : a)
      );
      toast.error("Failed to update status");
    }
  };

  const handleJobClick = (app) => {
    setSelectedJob(app);
  };

  const handleAddJobClick = () => {
    // Check if user is at limit
    if (quota?.is_at_limit) {
      setShowPaywallModal(true);
    } else {
      setShowAddModal(true);
    }
  };

  const handleJobAdded = (newApp) => {
    setApplications(prev => [newApp, ...prev]);
    fetchStats();
    fetchQuota(); // Refresh quota after adding job
  };

  const handleJobDeleted = (appId) => {
    setApplications(prev => prev.filter(a => a.id !== appId));
    setSelectedJob(null);
    fetchStats();
  };

  const handleStatusUpdate = (appId, newStatus) => {
    setApplications(prev => 
      prev.map(a => a.id === appId ? { ...a, status: newStatus } : a)
    );
    if (selectedJob && selectedJob.id === appId) {
      setSelectedJob(prev => ({ ...prev, status: newStatus }));
    }
    fetchStats();
  };

  const handlePaymentSuccess = (paymentData) => {
    // Update local quota state
    setQuota(prev => ({
      ...prev,
      jobs_limit: paymentData.new_jobs_limit,
      jobs_used: paymentData.jobs_used,
      is_at_limit: paymentData.jobs_used >= paymentData.new_jobs_limit,
    }));
    toast.success(`Successfully added ${paymentData.jobs_added} job credits!`);
  };

  const handleBuyCreditsClick = () => {
    setShowPaywallModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F1EC]">
        <Navbar 
          token={token} 
          quota={quota} 
          onQuotaUpdate={setQuota}
          onBuyCreditsClick={handleBuyCreditsClick}
        />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-[#A3C3D8]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F1EC]" data-testid="dashboard">
      <Navbar 
        token={token} 
        quota={quota} 
        onQuotaUpdate={setQuota}
        onBuyCreditsClick={handleBuyCreditsClick}
      />
      
      {/* Stats Bar */}
      {stats && (
        <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/50 px-6 py-4">
          <div className="max-w-[1800px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-[#A3C3D8]" />
                <span className="text-sm text-[#555F67]">
                  <span className="font-semibold text-[#2D3748]">{stats.total}</span> Total Jobs
                </span>
              </div>
              {COLUMNS.map(col => (
                <div key={col.id} className="flex items-center gap-2">
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: col.color }}
                  />
                  <span className="text-sm text-[#94A3B8]">
                    {stats[col.id.toLowerCase()] || 0} {col.title}
                  </span>
                </div>
              ))}
            </div>
            <Button
              onClick={handleAddJobClick}
              className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38] font-medium"
              data-testid="add-job-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Job
            </Button>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <div className="p-6">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-6 overflow-x-auto pb-8 snap-x" data-testid="kanban-board">
            {COLUMNS.map((column) => (
              <div 
                key={column.id} 
                className="min-w-[300px] w-[300px] flex flex-col bg-slate-50/50 rounded-xl p-4 border border-slate-100/50"
                data-testid={`column-${column.id.toLowerCase()}`}
              >
                {/* Column Header */}
                <div className="flex items-center justify-between mb-4 px-2">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: column.color }}
                    />
                    <h3 className="font-semibold text-[#555F67]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {column.title}
                    </h3>
                  </div>
                  <span className="text-sm text-[#94A3B8] font-medium">
                    {getColumnApplications(column.id).length}
                  </span>
                </div>

                {/* Droppable Area */}
                <Droppable droppableId={column.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 space-y-3 min-h-[200px] rounded-lg p-2 transition-colors ${
                        snapshot.isDraggingOver ? 'bg-[#A3C3D8]/10' : ''
                      }`}
                    >
                      {getColumnApplications(column.id).length === 0 ? (
                        <div className="empty-state text-center py-12">
                          <Building2 className="h-10 w-10 mx-auto text-[#94A3B8]/40 mb-3" />
                          <p className="text-sm text-[#94A3B8]">
                            No jobs in {column.title.toLowerCase()}
                          </p>
                          {column.id === "SAVED" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowAddModal(true)}
                              className="mt-2 text-[#4A728D] hover:text-[#355870]"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add first job
                            </Button>
                          )}
                        </div>
                      ) : (
                        getColumnApplications(column.id).map((app, index) => (
                          <Draggable key={app.id} draggableId={app.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                onClick={() => handleJobClick(app)}
                              >
                                <JobCard 
                                  application={app} 
                                  isDragging={snapshot.isDragging}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>
      </div>

      {/* Job Details Modal */}
      {selectedJob && (
        <JobDetailsModal
          application={selectedJob}
          onClose={() => setSelectedJob(null)}
          onDelete={handleJobDeleted}
          onStatusUpdate={handleStatusUpdate}
        />
      )}

      {/* Add Job Modal */}
      {showAddModal && (
        <AddJobModal
          onClose={() => setShowAddModal(false)}
          onJobAdded={handleJobAdded}
        />
      )}

      {/* Paywall Modal */}
      <PaywallModal
        open={showPaywallModal}
        onOpenChange={setShowPaywallModal}
        quota={quota}
        token={token}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
}
